/**
 * The copy layer. ADR-0005, NFR-18, NFR-19.
 *
 * v1.0 ships English only, but the shape is built for translation now because
 * retrofitting it later means touching every call site. Specifically:
 *
 *  - Interpolation uses NAMED placeholders, never positional and never string
 *    concatenation, because word order differs between languages.
 *  - `plural()` takes a locale and defers to `Intl.PluralRules`. English has
 *    two plural forms; Arabic has six; hand-rolled `n === 1` is wrong in most
 *    of the world.
 *  - `gender` is accepted as an interpolation value even though English never
 *    agrees with it. Turkish — the most likely second locale — has no
 *    grammatical gender at all, which is what makes NFR-09 easy there; Spanish,
 *    German and Arabic do. Adding this parameter later would mean revisiting
 *    every call.
 */
import { en } from './en'

export type Locale = 'en'

const catalogues = { en } as const

export const DEFAULT_LOCALE: Locale = 'en'

export type InterpolationValue = string | number

/**
 * Resolve a dotted key against the catalogue.
 *
 * Typed loosely on purpose: a fully typed dotted-path lookup costs a large
 * amount of type machinery for a guarantee the `no-raw-strings` lint rule and
 * the `copyKeys` test already provide more cheaply.
 */
function lookup(locale: Locale, key: string): string {
  const parts = key.split('.')
  let node: unknown = catalogues[locale]
  for (const part of parts) {
    if (typeof node !== 'object' || node === null || !(part in node)) {
      throw new Error(`Missing copy key: ${key}`)
    }
    node = (node as Record<string, unknown>)[part]
  }
  if (typeof node !== 'string') throw new Error(`Copy key is not a string: ${key}`)
  return node
}

const PLACEHOLDER = /\{(\w+)\}/g

export function interpolate(
  template: string,
  values: Record<string, InterpolationValue> = {},
): string {
  return template.replace(PLACEHOLDER, (match, name: string) => {
    const value = values[name]
    if (value === undefined) {
      // Loud in development, harmless in production: a missing value should be
      // caught in review, never rendered as a stray `{name}` to a user.
      if (import.meta.env.DEV) throw new Error(`Missing interpolation "${name}" in: ${template}`)
      return match
    }
    return String(value)
  })
}

export interface Translator {
  (key: string, values?: Record<string, InterpolationValue>): string
  locale: Locale
  /** Selects a plural form via Intl. Pass a catalogue object keyed by plural category. */
  plural(
    forms: Partial<Record<Intl.LDMLPluralRule, string>>,
    count: number,
    values?: Record<string, InterpolationValue>,
  ): string
  /** Locale-correct date formatting. Never hand-rolled — see NFR-08 and ADR-0005. */
  date(value: Date, options?: Intl.DateTimeFormatOptions): string
}

export function createTranslator(locale: Locale = DEFAULT_LOCALE): Translator {
  const t = ((key, values) => interpolate(lookup(locale, key), values)) as Translator

  t.locale = locale

  t.plural = (forms, count, values = {}) => {
    const category = new Intl.PluralRules(locale).select(count)
    const template = forms[category] ?? forms.other
    if (template === undefined) throw new Error(`No plural form for "${category}"`)
    return interpolate(template, { count, ...values })
  }

  t.date = (value, options = { dateStyle: 'long' }) =>
    new Intl.DateTimeFormat(locale, options).format(value)

  return t
}

/** The app is single-locale in v1.0, so one shared instance is correct. */
export const t = createTranslator(DEFAULT_LOCALE)

export { en }
export type { Copy } from './en'
